import { useMemo } from 'react';

import { gql, useApolloClient } from '@apollo/client';

import { MaterializedSegmentFragmentDoc } from '@spotify/confidence-graphql';

import { Criterion } from './types';

const SEGMENT_WITH_LABELS_FRAGMENT = gql`
  fragment SegmentWithLabels on FlagsAdminV1Segment {
    __typename
    name
    displayName
    labels {
      key
      value
    }
  }
`;

export const useResolveCriterionDisplayNames = (
  criteria: Record<string, Criterion>,
): Record<string, Criterion> => {
  const client = useApolloClient();

  return useMemo(() => {
    const resolved: Record<string, Criterion> = {};

    for (const [key, criterion] of Object.entries(criteria)) {
      if (criterion.segment?.segment) {
        const cached = client.readFragment({
          id: client.cache.identify({
            __typename: 'FlagsAdminV1Segment',
            name: criterion.segment.segment,
          }),
          fragment: SEGMENT_WITH_LABELS_FRAGMENT,
        });
        if (cached) {
          resolved[key] = {
            ...criterion,
            segment: {
              ...criterion.segment,
              displayName:
                criterion.segment.displayName ??
                cached.displayName ??
                undefined,
              labels: criterion.segment.labels ?? cached.labels ?? undefined,
            },
          };
          continue;
        }
      }

      if (
        criterion.materializedSegment?.materializedSegment &&
        !criterion.materializedSegment.displayName
      ) {
        const cached = client.readFragment({
          id: client.cache.identify({
            __typename: 'FlagsAdminV1MaterializedSegment',
            name: criterion.materializedSegment.materializedSegment,
          }),
          fragment: MaterializedSegmentFragmentDoc,
        });
        if (cached?.displayName) {
          resolved[key] = {
            ...criterion,
            materializedSegment: {
              ...criterion.materializedSegment,
              displayName: cached.displayName,
            },
          };
          continue;
        }
      }

      resolved[key] = criterion;
    }

    return resolved;
  }, [client, criteria]);
};
